import { t } from "@/lib/i18n/interpolate";
import type { DocSection, DocumentModel } from "@/lib/tool-kit/document";
import { allocateWords } from "@/lib/tool-kit/essay-templates/research-proposal";
import {
  countWords,
  paragraphsOrPrompt,
  splitLines,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A Three Minute Thesis script planner.
 *
 * The University of Queensland's competition rules fix the hard limits: three minutes at
 * most (going over is disqualification), spoken word, one static slide shown from the
 * first word, no props and no extra media. Everything else here is a planning aid: a
 * speaking pace the writer chooses, a safety margin the writer chooses, and a typical
 * split of the talk across five beats that follow UQ's judging questions (motivation and
 * background, strategy, findings, conclusions and impact). The tool counts the writer's
 * own words and converts them to time at that pace; it does not write the script.
 */

export const LIMIT_SECONDS = 180;
export const MAX_MARGIN_SECONDS = 60;
export const DEFAULT_MARGIN_SECONDS = 10;
export const MIN_WPM = 60;
export const MAX_WPM = 220;
export const LONG_SENTENCE_WORDS = 25;

export type PaceKey = "deliberate" | "conversational" | "brisk" | "custom";

/** Planning paces, in words per minute. Not a standard: 3MT sets no word limit. */
export const PACE_WPM: Record<Exclude<PaceKey, "custom">, number> = {
  deliberate: 110,
  conversational: 130,
  brisk: 150,
};

export const BEATS = [
  "hook",
  "question",
  "method",
  "findings",
  "impact",
] as const;
export type Beat = (typeof BEATS)[number];

/** A typical split of the talk, in percent. A convention for pacing, not a rule. */
export const BEAT_SHARES: Record<Beat, number> = {
  hook: 15,
  question: 20,
  method: 20,
  findings: 25,
  impact: 20,
};

export const SLIDE_ITEMS = [
  "single",
  "static",
  "media",
  "props",
  "start",
  "legible",
  "oneIdea",
  "adds",
] as const;
export type SlideItem = (typeof SLIDE_ITEMS)[number];

/** The first five are UQ rules; the rest are advice. */
export const SLIDE_RULES: ReadonlySet<SlideItem> = new Set([
  "single",
  "static",
  "media",
  "props",
  "start",
]);

export interface ThreeMinuteInput {
  title: string;
  pace: PaceKey;
  customWpm: string;
  margin: string;
  beats: Record<Beat, string>;
  slide: Record<SlideItem, boolean>;
}

export function emptySlide(): Record<SlideItem, boolean> {
  return Object.fromEntries(SLIDE_ITEMS.map((item) => [item, false])) as Record<
    SlideItem,
    boolean
  >;
}

/** Whole seconds in 0–60; blank or nonsense means the default margin. */
export function marginSeconds(raw: string): number {
  const value = Number.parseInt(raw.trim(), 10);
  if (!Number.isFinite(value) || value < 0 || value > MAX_MARGIN_SECONDS) {
    return DEFAULT_MARGIN_SECONDS;
  }
  return value;
}

export function paceWpm(input: Pick<ThreeMinuteInput, "pace" | "customWpm">) {
  if (input.pace !== "custom") {
    return PACE_WPM[input.pace];
  }
  const value = Number.parseInt(input.customWpm.trim(), 10);
  if (!Number.isFinite(value) || value < MIN_WPM || value > MAX_WPM) {
    return PACE_WPM.conversational;
  }
  return value;
}

export function wordsToSeconds(words: number, wpm: number): number {
  return (words / wpm) * 60;
}

/** "2:42" — whole seconds, rounded to the nearest. */
export function formatClock(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, "0")}`;
}

/**
 * Words that fit in a time at a pace, rounded down: a script that is a word short costs
 * nothing, one that is a word long can cost the round.
 */
export function wordBudget(seconds: number, wpm: number): number {
  return Math.floor((seconds * wpm) / 60);
}

/** A sentence ends at . ! ? or … plus any closing bracket or quote, then a space. */
const SENTENCE_END = /(?<=[.!?…]["”’)\]]*)\s+/;

/** The word count of the longest sentence in a text; 0 for an empty one. */
export function longestSentence(text: string): number {
  return Math.max(
    0,
    ...splitLines(text)
      .flatMap((line) => line.split(SENTENCE_END))
      .map(countWords)
  );
}

export type TimingState = "empty" | "fits" | "tight" | "over";

export interface ThreeMinuteAnalysis {
  wpm: number;
  margin: number;
  /** The speaking budget: three minutes less the writer's margin. */
  budgetSeconds: number;
  budgetWords: number;
  words: Record<Beat, number>;
  targetWords: Record<Beat, number>;
  totalWords: number;
  totalSeconds: number;
  /** Seconds left against the budget; negative when over it. */
  spare: number;
  state: TimingState;
  longest: number;
  /** Beats with no text yet. */
  missing: Beat[];
}

function stateOf(
  totalWords: number,
  seconds: number,
  budget: number
): TimingState {
  if (totalWords === 0) {
    return "empty";
  }
  if (seconds <= budget) {
    return "fits";
  }
  return seconds <= LIMIT_SECONDS ? "tight" : "over";
}

export function analyzeThreeMinute(
  input: ThreeMinuteInput
): ThreeMinuteAnalysis {
  const wpm = paceWpm(input);
  const margin = marginSeconds(input.margin);
  const budgetSeconds = LIMIT_SECONDS - margin;
  const budgetWords = wordBudget(budgetSeconds, wpm);
  const words = Object.fromEntries(
    BEATS.map((beat) => [beat, countWords(input.beats[beat])])
  ) as Record<Beat, number>;
  const allocated = allocateWords(
    budgetWords,
    BEATS.map((beat) => BEAT_SHARES[beat]),
    1
  );
  const targetWords = Object.fromEntries(
    BEATS.map((beat, index) => [beat, allocated[index]])
  ) as Record<Beat, number>;
  const totalWords = BEATS.reduce((sum, beat) => sum + words[beat], 0);
  const totalSeconds = wordsToSeconds(totalWords, wpm);
  return {
    wpm,
    margin,
    budgetSeconds,
    budgetWords,
    words,
    targetWords,
    totalWords,
    totalSeconds,
    spare: budgetSeconds - totalSeconds,
    state: stateOf(totalWords, totalSeconds, budgetSeconds),
    longest: Math.max(
      0,
      ...BEATS.map((beat) => longestSentence(input.beats[beat]))
    ),
    missing: BEATS.filter((beat) => input.beats[beat].trim() === ""),
  };
}

export type CheckTextKey =
  | "fits_todo"
  | "fits_done"
  | "fits_tight"
  | "fits_over"
  | "beats_done"
  | "beats_todo"
  | "sentences_done"
  | "sentences_note";

export interface ThreeMinuteCheck {
  id: "fits" | "beats" | "sentences";
  /** Which sentence in the widget's strings describes this result. */
  textKey: CheckTextKey;
  state: "done" | "todo" | "note";
}

const FITS_CHECK: Record<TimingState, ThreeMinuteCheck> = {
  empty: { id: "fits", textKey: "fits_todo", state: "todo" },
  fits: { id: "fits", textKey: "fits_done", state: "done" },
  tight: { id: "fits", textKey: "fits_tight", state: "note" },
  over: { id: "fits", textKey: "fits_over", state: "note" },
};

/** Timing, completeness and spoken readability, in that order. */
export function threeMinuteChecks(
  analysis: ThreeMinuteAnalysis
): ThreeMinuteCheck[] {
  const complete = analysis.missing.length === 0;
  const readable = analysis.longest <= LONG_SENTENCE_WORDS;
  return [
    FITS_CHECK[analysis.state],
    {
      id: "beats",
      textKey: complete ? "beats_done" : "beats_todo",
      state: complete ? "done" : "todo",
    },
    {
      id: "sentences",
      textKey: readable ? "sentences_done" : "sentences_note",
      state: readable ? "done" : "note",
    },
  ];
}

/* ------------------------------------------------------------------ document */

export interface ThreeMinuteLabels {
  defaultTitle: string;
  subtitle: string;
  beatHeadings: Record<Beat, string>;
  beatTips: Record<Beat, string>;
  beatPrompts: Record<Beat, string>;
  target: string;
  yours: string;
  timingHeading: string;
  timingHead: { beat: string; target: string; words: string; time: string };
  timingTotal: string;
  timingNote: string;
  slideHeading: string;
  slideIntro: string;
  slideItems: Record<SlideItem, string>;
  ruleTag: string;
  adviceTag: string;
}

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

function beatSection(
  beat: Beat,
  input: ThreeMinuteInput,
  analysis: ThreeMinuteAnalysis,
  labels: ThreeMinuteLabels,
  fmt: Format
): DocSection {
  const target = t(labels.target, {
    words: fmt(analysis.targetWords[beat]),
    time: formatClock(wordsToSeconds(analysis.targetWords[beat], analysis.wpm)),
  });
  const written = analysis.words[beat];
  const status =
    written > 0
      ? ` ${t(labels.yours, {
          words: fmt(written),
          time: formatClock(wordsToSeconds(written, analysis.wpm)),
        })}`
      : "";
  return {
    heading: labels.beatHeadings[beat],
    blocks: [
      { kind: "guidance", text: `${target}${status} ${labels.beatTips[beat]}` },
      ...paragraphsOrPrompt(input.beats[beat], labels.beatPrompts[beat]),
    ],
  };
}

function timingSection(
  analysis: ThreeMinuteAnalysis,
  labels: ThreeMinuteLabels,
  fmt: Format
): DocSection {
  const head = labels.timingHead;
  const rows = BEATS.map((beat) => [
    labels.beatHeadings[beat],
    `${fmt(analysis.targetWords[beat])}`,
    fmt(analysis.words[beat]),
    formatClock(wordsToSeconds(analysis.words[beat], analysis.wpm)),
  ]);
  rows.push([
    labels.timingTotal,
    fmt(analysis.budgetWords),
    fmt(analysis.totalWords),
    formatClock(analysis.totalSeconds),
  ]);
  return {
    heading: labels.timingHeading,
    blocks: [
      { kind: "guidance", text: labels.timingNote },
      {
        kind: "table",
        head: [head.beat, head.target, head.words, head.time],
        rows,
      },
    ],
  };
}

function slideSection(
  input: ThreeMinuteInput,
  labels: ThreeMinuteLabels
): DocSection {
  return {
    heading: labels.slideHeading,
    blocks: [
      { kind: "guidance", text: labels.slideIntro },
      {
        kind: "checklist",
        items: SLIDE_ITEMS.map((item) => ({
          text: `${labels.slideItems[item]} (${
            SLIDE_RULES.has(item) ? labels.ruleTag : labels.adviceTag
          })`,
          checked: input.slide[item],
        })),
      },
    ],
  };
}

export function buildThreeMinuteDocument(
  input: ThreeMinuteInput,
  labels: ThreeMinuteLabels,
  fmt: Format
): DocumentModel {
  const analysis = analyzeThreeMinute(input);
  return {
    title: input.title.trim() || labels.defaultTitle,
    subtitle: t(labels.subtitle, {
      wpm: fmt(analysis.wpm),
      time: formatClock(analysis.totalSeconds),
      limit: formatClock(LIMIT_SECONDS),
    }),
    sections: [
      ...BEATS.map((beat) => beatSection(beat, input, analysis, labels, fmt)),
      timingSection(analysis, labels, fmt),
      slideSection(input, labels),
    ],
  };
}
